export const EVENT_REQUEST_TIMEOUT_MS = 8_000;
/** How long an awaited event may hold its caller, who is usually about to leave the page. */
export const EVENT_FLUSH_BUDGET_MS = 1_500;
export const EVENT_RETRY_DELAYS_MS = [2_000, 5_000, 15_000, 30_000];
const MAX_PENDING_EVENTS = 100;
const pending = new Set<() => Promise<void>>();

class EventRejected extends Error {
    constructor(readonly status: number) {
        super(`Event rejected with HTTP ${status}`);
    }
}

const retryable = (error: unknown) => {
    if (error instanceof EventRejected) {
        return error.status === 408 || error.status === 429 || error.status >= 500;
    }
    // No answer at all: the network failed or the request timed out.
    return error instanceof TypeError || (error instanceof DOMException && error.name === 'AbortError');
};

const reportDropped = () => console.warn('Analytics event delivery failed');

/** POSTs an event with `keepalive`, so one sent right before the page is left (Google sign-in,
 * Stripe, logout) is still delivered after the unload. */
export async function postEvent(url: string, body: string, headers: Record<string, string>): Promise<void> {
    // Not AbortSignal.timeout(): Safari before 16 lacks it.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), EVENT_REQUEST_TIMEOUT_MS);
    try {
        const response = await fetch(url, {method: 'POST', headers, body, keepalive: true, signal: controller.signal});
        if (!response.ok) throw new EventRejected(response.status);
    } finally {
        clearTimeout(timer);
    }
}

/** Settles when `delivery` does or the flush budget runs out. Does not cancel the delivery,
 * which is keepalive and outlives the page if the caller leaves it. */
export function withinFlushBudget(delivery: Promise<unknown>): Promise<void> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    return Promise.race([
        delivery.then(() => undefined),
        new Promise<void>(resolve => { timer = setTimeout(resolve, EVENT_FLUSH_BUDGET_MS); }),
    ]).finally(() => clearTimeout(timer));
}

/** Keep short network outages out of the funnel. The caller freezes both the
 * payload/event ID and its auth identity; a retry must not become a new event
 * or move an anonymous action onto whichever account signed in meanwhile.
 * Retries live only in memory and never hold up a redirect or a user action.
 */
export async function deliverEvent(send: () => Promise<void>): Promise<void> {
    try {
        return await send();
    } catch (error) {
        if (!retryable(error) || pending.size >= MAX_PENDING_EVENTS) {
            reportDropped();
            return;
        }
    }
    pending.add(send);
    void (async () => {
        try {
            for (const delay of EVENT_RETRY_DELAYS_MS) {
                await new Promise(resolve => setTimeout(resolve, delay));
                try {
                    await send();
                    return;
                } catch (error) {
                    if (!retryable(error)) break;
                }
            }
            reportDropped();
        } finally {
            pending.delete(send);
        }
    })();
}
